import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Maximize2, Download } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Reveal, SectionHeading } from "./Reveal";
import { PLAN_ITEMS } from "../data/media";
import { ImageLightbox } from "./ImageLightbox";

export const Plans = () => {
  const { t, lang } = useLang();
  const [cat, setCat] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  const filtered = useMemo(
    () => (cat === 0 ? PLAN_ITEMS : PLAN_ITEMS.filter((p) => p.category === cat)),
    [cat]
  );

  const lightboxItems = filtered.map((p) => ({ src: p.src, caption: p.title[lang] || p.title.tr }));

  return (
    <section id="plans" className="relative py-24 md:py-32 bg-[#0D0D0D] border-y border-[#1c1c1c]" data-testid="plans-section">
      <div className="px-6 md:px-12 lg:px-20">
        <SectionHeading overline={t.plans.overline} title={t.plans.title} text={t.plans.text} />

        {/* Category filters */}
        <Reveal delay={0.1}>
          <div className="mt-12 flex flex-wrap gap-2">
            {t.plans.categories.map((c, i) => (
              <button
                key={i}
                onClick={() => setCat(i)}
                className={`px-4 py-2 text-xs uppercase tracking-[0.13em] border transition-colors duration-300 ${
                  cat === i
                    ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5"
                    : "border-[#2E2E2E] text-[#FFFFF0]/55 hover:border-white/30"
                }`}
                data-testid={`plans-category-${i}`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        {/* Grid */}
        <motion.div layout className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {filtered.map((plan, i) => (
              <motion.div
                key={plan.title.tr + i}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.4 }}
                className="group bg-[#141414] border border-[#2E2E2E] hover:border-[#D4AF37]/50 transition-colors duration-500"
                data-testid={`plan-card-${i}`}
              >
                <div className="relative aspect-[4/3] overflow-hidden cursor-zoom-in" onClick={() => setLightbox(i)}>
                  <img src={plan.src} alt={plan.title[lang] || plan.title.tr} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors" />
                  <span className="absolute top-3 start-3 text-[0.62rem] uppercase tracking-[0.14em] bg-black/70 text-[#D4AF37] px-2 py-1">
                    {t.plans.categories[plan.category]}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-serif text-xl font-light text-[#FFFFF0]">{plan.title[lang] || plan.title.tr}</h3>
                  <p className="mt-1 text-xs text-[#FFFFF0]/45 font-light">{t.plans.sourceNote}</p>
                  <div className="mt-4 flex items-center gap-4">
                    <button
                      onClick={() => setLightbox(i)}
                      className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-[#FFFFF0]/70 hover:text-[#D4AF37] transition-colors"
                      data-testid={`plan-fullscreen-${i}`}
                    >
                      <Maximize2 size={14} strokeWidth={1.5} /> {t.plans.fullscreen}
                    </button>
                    <a
                      href={plan.src}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-[#FFFFF0]/70 hover:text-[#D4AF37] transition-colors"
                      data-testid={`plan-download-${i}`}
                    >
                      <Download size={14} strokeWidth={1.5} /> {t.plans.download}
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      {lightbox !== null && (
        <ImageLightbox
          items={lightboxItems}
          index={lightbox}
          onClose={() => setLightbox(null)}
          onPrev={() => setLightbox((p) => (p - 1 + filtered.length) % filtered.length)}
          onNext={() => setLightbox((p) => (p + 1) % filtered.length)}
          downloadLabel={t.plans.download}
        />
      )}
    </section>
  );
};
